import * as angular from "angular"
import { SoilAnalysisViewer } from "../../../map/viewer/soil"
import { INitrogenAnalysis, INitrogenAnalysisAlloc } from "../../../nitrogen/types"
import { calculateTotalSns, sortNitrogenAnalysis } from "../../../nitrogen/utils"
import { datalayers, FieldViewModel, groupBy, IDatalayerViewerFactory, MapGeometryType, models, SoilMapView, SoilUnit } from "../../../omnia"
import { IDataLayer, IDataLayerType, IDataLayerZone, ILayerViewModel, LayerTypeId } from "../../../types"
import { groupMapFeaturesInSingleLayer } from "../context"
import { createMapViewGroup } from "../group"
import { IViewerContext } from "../types"

var nitrogenColors = [
    { Value: 0, Colour: [108, 182, 255], Index: 0 },
    { Value: 60, Colour: [90, 159, 255], Index: 1 },
    { Value: 80, Colour: [72, 133, 255], Index: 2 },
    { Value: 100, Colour: [54, 104, 255], Index: 3 },
    { Value: 120, Colour: [35, 72, 255], Index: 4 },
    { Value: 160, Colour: [17, 37, 255], Index: 5 },
    { Value: 240, Colour: [0, 0, 255], Index: 6 },
];


export function createNitrogenGroup(ctxt: IViewerContext) {

    const { soilSvc } = ctxt

    let analysis = soilSvc.getNitrogenAnalysis(true);

    if (analysis && analysis.length) {

        let nview = new NitrogenAnalysisView(ctxt, analysis, () => soilSvc.units.nitrogen)
        return createMapViewGroup("N", "Nitrogen analysis", [nview])

    }

}


export class NitrogenAnalysisView extends SoilMapView {

    private layerViewer: IDatalayerViewerFactory;

    constructor(private ctxt: IViewerContext, analysis: INitrogenAnalysis[], units: () => SoilUnit) {

        super("Nitrogen", "N");

        this.layerViewer = SoilAnalysisViewer.getFactory("SNS", units, { name: "N", colors: nitrogenColors });

        this.setupColors(nitrogenColors);

        this.setupUnits([
            { id: SoilUnit.KgHa, label: "kg/ha" },
            { id: SoilUnit.Index, label: "Index" },
        ], units);

        this._layers = this.createVirtualLayers(analysis);

    }

    private createVirtualLayers(analysis: INitrogenAnalysis[]) {

        let soilAnalysisLayerType = this.ctxt.soilSvc.getLayerType(LayerTypeId.SoilAnalysis);

        let cropping = this.ctxt.soilSvc.cropping.filter(c => c.hasBoundary() && c.currentYear);

        return cropping.map(c => {

            let subset = analysis.filter(a => a.Allocations && (a.Allocations.length > 0))
                .map(a => {
                    return {
                        analysis: a,
                        allocations: a.Allocations.filter(a => (a.CroppingId == c.croppingId) && (!a.ZoneId))
                    }
                })
                .filter(a => a.allocations.length > 0)
                .sort((a1, a2) => {
                    return sortNitrogenAnalysis(c, a1.analysis, a2.analysis)
                });

            let croppingAnalysis = subset.length ? subset[0] : null;

            if (croppingAnalysis) {

                let analysis = croppingAnalysis.analysis;
                let alloc = croppingAnalysis.allocations[0];

                let datalayer = this.createVirtualLayer(c, analysis, alloc, soilAnalysisLayerType);

                let options = {
                    addToMap: !groupMapFeaturesInSingleLayer,
                };

                let layerVm = this.ctxt.createLayerViewModel(datalayer, c.fieldVm, options);

                layerVm.selectable = true;
                layerVm.showTooltips = false;

                return layerVm;

            }

        }).filter(l => !!l);

    }

    private createVirtualLayer(cropping: FieldViewModel, analysis: INitrogenAnalysis, alloc: INitrogenAnalysisAlloc, soilAnalysisLayerType: IDataLayerType) {

        analysis = angular.copy(analysis);
        analysis["SNS"] = calculateTotalSns(analysis, alloc);

        let zones: IDataLayerZone[] = [

            {
                DataLayerZoneId: 0,
                Number: 0,
                Geometry: cropping.cropping.Boundary,
                Value: {
                    Data: analysis,
                    Name: analysis.AnalysisNumber,
                    Value: analysis.NitrogenAnalysisId
                },
                Data: analysis,
                Name: cropping.fieldName
            }

        ];

        let layer: IDataLayer = {
            DataLayerId: 0,
            GeometryType: MapGeometryType.Polygon,
            TypeId: LayerTypeId.SoilAnalysis,
            Type: soilAnalysisLayerType,
            FieldId: cropping.fieldId,
            CroppingId: cropping.croppingId,
            Zones: zones
        }

        return layer;

    }

    protected setCurrentUnit(unit: SoilUnit) {
        super.setCurrentUnit(unit);
        this.ctxt.soilSvc.units.nitrogen = unit;
    }

    protected findLayers(fieldId: number, croppingId: number): ILayerViewModel[] {

        if (croppingId) {
            return super.findLayers(fieldId, croppingId);
        }

        let layers = groupBy(this.getLayers(), l => l, l => l.fieldId)
            .filter(i => i.values.length == 1).map(i => i.values[0])
            .filter(l => l.fieldId == fieldId);

        return layers;

    }

    protected showLayer(l: ILayerViewModel, visibilityFilter?: (l: ILayerViewModel) => boolean) {
        l.setViewer(this.layerViewer)
        super.showLayer(l, visibilityFilter)
    }

}
